/**
 * @file OrderReturnCard.tsx
 * @description Карточка возврата во вкладке «Возвраты» заказа (вынесена из
 * `OrderReturnsTab`, phase19.31): статус, причина, сумма «₽», время по поясу
 * магазина и кнопки следующего шага.
 *
 * Кнопки шага — реестром `RETURN_NEXT_ACTIONS` по статусу (а не каскадом
 * условий): новый шаг процесса возврата = новая запись. «Отказать» требует
 * причину — её спрашивает вкладка в окне кабинета.
 */

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import {
  currencySymbol,
  formatMoney,
  RETURN_STATUS_LABEL,
  RETURN_TYPE_LABEL,
  type ReturnDto,
  type ReturnStatus,
} from "../../lib/api-orders";
import { formatShopDateTime } from "../../lib/shop-time";

/** Шаг процесса возврата, доступный из карточки. */
export type ReturnStep = "approve" | "reject" | "inspect" | "approveRefund" | "complete";

/** Кнопки шага по статусу возврата; `danger` — красная кнопка. */
const RETURN_NEXT_ACTIONS: Partial<Record<ReturnStatus, { step: ReturnStep; label: string; danger?: boolean }[]>> = {
  REQUESTED: [
    { step: "approve", label: "Одобрить" },
    { step: "reject", label: "Отказать", danger: true },
  ],
  APPROVED: [{ step: "inspect", label: "Принять товар" }],
  AWAITING_GOODS: [{ step: "approveRefund", label: "К возврату средств" }],
  APPROVED_REFUND: [{ step: "complete", label: "Завершить" }],
};

/** Вариант пилюли статуса: завершён — основной, отказ — красный, остальное — спокойный. */
function statusVariant(status: ReturnStatus): "default" | "destructive" | "secondary" {
  if (status === "COMPLETED") return "default";
  if (status === "REJECTED") return "destructive";
  return "secondary";
}

interface Props {
  item: ReturnDto;
  currency: string;
  rowAttr: { "data-row-id": string };
  highlighted: boolean;
  /** Кнопки шага — только с правом обработки возвратов. */
  canProcess: boolean;
  acting: boolean;
  onStep: (step: ReturnStep) => void;
}

export function OrderReturnCard({ item: r, currency, rowAttr, highlighted, canProcess, acting, onStep }: Props) {
  const actions = canProcess ? (RETURN_NEXT_ACTIONS[r.status] ?? []) : [];
  return (
    <EditorialCard {...rowAttr} className={highlighted ? "ring-2 ring-primary ring-offset-2" : undefined}>
      <EditorialCard.Body>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(r.status)}>{RETURN_STATUS_LABEL[r.status]}</Badge>
              <Badge variant="outline">{RETURN_TYPE_LABEL[r.type]}</Badge>
              {/* Номер возврата (phase19.24 §12 п.1) — обычным шрифтом (SC43). */}
              {r.number ? <span className="type-body tabular-nums text-on-surface-variant">{r.number}</span> : null}
            </div>
            <div className="type-body mt-1 font-semibold tabular-nums text-on-surface">
              К возврату: {formatMoney(r.refundAmount)} {currencySymbol(currency)}
            </div>
            <p className="type-caption mt-1 text-on-surface-variant">
              {r.itemsCount} поз. · {formatShopDateTime(r.requestedAt)}
            </p>
            {r.customerComment ? (
              <p className="type-body mt-2">
                <span className="text-on-surface-variant">Клиент:</span> {r.customerComment}
              </p>
            ) : null}
            {r.rejectedReason ? (
              <p className="type-body mt-1 text-[var(--status-error-fg,var(--destructive))]">Отказ: {r.rejectedReason}</p>
            ) : null}
          </div>
          {actions.length > 0 ? (
            <div className="flex shrink-0 flex-col gap-2">
              {actions.map((action) => (
                <Button
                  key={action.step}
                  variant={action.danger ? "destructive" : "outline"}
                  onClick={() => onStep(action.step)}
                  disabled={acting}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
